import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form, FormField, FormItem, FormControl, FormLabel, FormMessage } from "@/components/ui/form";
import { supabase } from "@/integrations/supabase/client";
import { useNotify } from "@/hooks/useNotify";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Edit, Trash2, Plus, ExternalLink, Calendar, Eye, MapPin } from "lucide-react";
import EntityActions, { EntityAction } from "@/components/shared/EntityActions";
import { formatDistance } from "date-fns";
import { useNavigate } from "react-router-dom";
import SiteAddressFields from "@/components/ui/site-address-fields";

interface CustomerProjectsProps {
  customerId: string;
}

interface Project {
  id: string;
  customer_id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  created_by: string;
  expected_start_date: string | null;
  expected_end_date: string | null;
  status: string | null;
  use_separate_site_address?: boolean;
  site_street_address?: string | null;
  site_suburb?: string | null;
  site_city?: string | null;
  site_province_state?: string | null;
  site_postal_code?: string | null;
}

const formSchema = z.object({
  name: z.string().min(1, "Project name is required").max(100, "Project name is too long"),
  description: z.string().max(500, "Description is too long").optional(),
  use_separate_site_address: z.boolean().default(false),
  site_street_address: z.string().optional(),
  site_suburb: z.string().optional(),
  site_city: z.string().optional(),
  site_province_state: z.string().optional(),
  site_postal_code: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

const CustomerProjects: React.FC<CustomerProjectsProps> = ({ customerId }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const notify = useNotify();
  const navigate = useNavigate();
  
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      use_separate_site_address: false,
      site_street_address: "",
      site_suburb: "",
      site_city: "",
      site_province_state: "",
      site_postal_code: "",
    },
  });

  useEffect(() => {
    loadProjects();
  }, [customerId]);

  useEffect(() => {
    if (editingProject) {
      form.reset({
        name: editingProject.name,
        description: editingProject.description || "",
        use_separate_site_address: editingProject.use_separate_site_address || false,
        site_street_address: editingProject.site_street_address || "",
        site_suburb: editingProject.site_suburb || "",
        site_city: editingProject.site_city || "",
        site_province_state: editingProject.site_province_state || "",
        site_postal_code: editingProject.site_postal_code || "",
      });
    } else {
      form.reset({
        name: "",
        description: "",
        use_separate_site_address: false,
        site_street_address: "",
        site_suburb: "",
        site_city: "",
        site_province_state: "",
        site_postal_code: "",
      });
    }
  }, [editingProject, form]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("projects")
        .select("*")
        .eq("customer_id", customerId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching projects:", error);
        notify.error(error);
        return;
      }

      setProjects(data || []);
    } catch (error) {
      console.error("Error in loadProjects:", error);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (formData: FormData) => {
    try {
      const userResponse = await supabase.auth.getUser();
      const userId = userResponse.data.user?.id;

      if (!userId) {
        notify.error("You must be logged in to add projects.");
        return;
      }

      let result;
      
      if (editingProject) {
        result = await supabase
          .from("projects")
          .update({
            name: formData.name,
            description: formData.description,
            use_separate_site_address: formData.use_separate_site_address,
            site_street_address: formData.use_separate_site_address ? formData.site_street_address : null,
            site_suburb: formData.use_separate_site_address ? formData.site_suburb : null,
            site_city: formData.use_separate_site_address ? formData.site_city : null,
            site_province_state: formData.use_separate_site_address ? formData.site_province_state : null,
            site_postal_code: formData.use_separate_site_address ? formData.site_postal_code : null,
            updated_at: new Date().toISOString(),
          })
          .eq("id", editingProject.id);
      } else {
        // Get user's company_id for the project
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('company_id')
          .eq('id', userId)
          .single();

        if (profileError || !profileData?.company_id) {
          throw new Error('User company not found');
        }

        result = await supabase.from("projects").insert({
          customer_id: customerId,
          name: formData.name,
          description: formData.description,
          company_id: profileData.company_id,
          use_separate_site_address: formData.use_separate_site_address,
          site_street_address: formData.use_separate_site_address ? formData.site_street_address : null,
          site_suburb: formData.use_separate_site_address ? formData.site_suburb : null,
          site_city: formData.use_separate_site_address ? formData.site_city : null,
          site_province_state: formData.use_separate_site_address ? formData.site_province_state : null,
          site_postal_code: formData.use_separate_site_address ? formData.site_postal_code : null,
          created_by: userId,
        });
      }

      if (result.error) {
        notify.error(result.error);
        return;
      }

      notify.success(`Project ${editingProject ? 'updated' : 'added'} successfully`);

      form.reset();
      setDialogOpen(false);
      setEditingProject(null);
      loadProjects();
    } catch (error) {
      console.error(`Error ${editingProject ? 'updating' : 'adding'} project:`, error);
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    try {
      const { error } = await supabase
        .from("projects")
        .delete()
        .eq("id", projectId);

      if (error) {
        notify.error(error);
        return;
      }

      notify.success("Project deleted successfully");

      loadProjects();
    } catch (error) {
      console.error("Error deleting project:", error);
    }
  };

  const handleEdit = (project: Project) => {
    setEditingProject(project);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingProject(null);
    form.reset();
  };

  const handleProjectClick = (projectId: string) => {
    navigate(`/projects/${projectId}`);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return formatDistance(date, new Date(), { addSuffix: true });
    } catch (error) {
      return "Invalid date";
    }
  };

  const formatDateRange = (startDate: string | null, endDate: string | null) => {
    if (!startDate && !endDate) return "No dates set";
    
    const formatDateStr = (dateStr: string) => {
      try {
        return format(new Date(dateStr), 'dd MMMM yyyy');
      } catch {
        return "Invalid date";
      }
    };

    if (startDate && endDate) {
      return `${formatDateStr(startDate)} - ${formatDateStr(endDate)}`;
    } else if (startDate) {
      return `Starts: ${formatDateStr(startDate)}`;
    } else {
      return `Ends: ${formatDateStr(endDate)}`;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Projects</h3>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => setEditingProject(null)}>
              <Plus className="mr-2 h-4 w-4" /> Add Project
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingProject ? "Edit Project" : "Add New Project"}</DialogTitle>
              <DialogDescription>
                {editingProject
                  ? "Update the project details below"
                  : "Enter the details for the new project"}
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Project Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter project name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description (Optional)</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Enter project description..."
                          className="min-h-[100px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <SiteAddressFields form={form} />
                
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={handleDialogClose}>
                    Cancel
                  </Button>
                  <Button type="submit">
                    {editingProject ? "Update Project" : "Add Project"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
        </div>
      ) : projects.length === 0 ? (
        <div className="text-center py-8 bg-muted/30 rounded-lg">
          <p className="text-muted-foreground">No projects found for this customer.</p>
          <Button 
            variant="outline"
            className="mt-4"
            onClick={() => setDialogOpen(true)}
          >
            <Plus className="mr-2 h-4 w-4" /> Add Project
          </Button>
        </div>
      ) : (
        <>
          {/* Mobile card view */}
          <div className="space-y-3 sm:hidden">
            {projects.map((project) => {
              const projectActions: EntityAction[] = [
                {
                  label: "View",
                  icon: Eye,
                  onClick: () => handleProjectClick(project.id),
                },
                {
                  label: "Edit",
                  icon: Edit,
                  onClick: () => handleEdit(project),
                },
                {
                  label: "Delete",
                  icon: Trash2,
                  onClick: () => handleDeleteProject(project.id),
                  destructive: true,
                  requiresConfirmation: {
                    title: "Delete Project?",
                    description: "This will permanently delete this project.",
                  },
                },
              ];

              const siteAddress = [
                project.site_street_address,
                project.site_suburb,
                project.site_city,
                project.site_province_state,
                project.site_postal_code,
              ].filter(Boolean).join(', ');

              return (
                <Card
                  key={project.id}
                  className="cursor-pointer hover:bg-muted/50 overflow-hidden"
                  onClick={() => handleProjectClick(project.id)}
                >
                  <CardContent className="p-3 space-y-2">
                    {/* Header: Name + Status + Actions */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">{project.name}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${
                          project.status === 'completed'
                            ? 'bg-green-100 text-green-800'
                            : project.status === 'in_progress'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {project.status || 'Active'}
                        </span>
                        <div onClick={(e) => e.stopPropagation()}>
                          <EntityActions actions={projectActions} size="sm" />
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    {project.description && (
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {project.description}
                      </p>
                    )}

                    {/* Details row */}
                    <div className="space-y-1.5 text-xs text-muted-foreground border-t pt-2">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="h-3 w-3 shrink-0" />
                        <span className="truncate">{formatDateRange(project.expected_start_date, project.expected_end_date)}</span>
                      </div>

                      {project.use_separate_site_address && siteAddress && (
                        <div className="flex items-center gap-1.5">
                          <MapPin className="h-3 w-3 shrink-0" />
                          <span className="truncate">{siteAddress}</span>
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <span>Created {formatDate(project.created_at)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Desktop table view */}
          <Card className="hidden sm:block">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Schedule</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projects.map((project) => (
                    <TableRow
                      key={project.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleProjectClick(project.id)}
                    >
                      <TableCell className="font-medium">{project.name}</TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {project.description || "No description"}
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          <span>{formatDateRange(project.expected_start_date, project.expected_end_date)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          project.status === 'completed'
                            ? 'bg-green-100 text-green-800'
                            : project.status === 'in_progress'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {project.status || 'Active'}
                        </span>
                      </TableCell>
                      <TableCell>{formatDate(project.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleProjectClick(project.id);
                            }}
                            title="View project details"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(project);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete this project.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteProject(project.id)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default CustomerProjects;
